import { type ReactElement } from 'react'
import { isNotEmpty } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Group, Text, TextInput } from '@mantine/core'
import { toShowingCountText, useSafeCurrentWorkflow, useTranslation } from '@front/model'
import { ButtonRefresh, NotReady, Page, WorkflowSelect } from '@components'
import { useAccountDrawer } from '@hooks'
import { AccountModal } from '../areaManagement/accountDetail/AccountModal'
import { useInfiniteServerState } from './useInfiniteServerState'
import { useClientState } from './useClientState'
import { ToggleActivityColumns } from './ToggleActivityColumns'
import { DownloadActivityCsv } from './DownloadCsv'
import { TanStackActivityTable } from './ActivityTable'
import { ActivityFilters } from './ActivityFilters'

function Activity(): ReactElement {
  const { t } = useTranslation()

  const wfContext = useSafeCurrentWorkflow()

  if (!wfContext) return <NotReady error={t('No workflow found')} />

  return <ActivityContent />
}

const ActivityContent = (): ReactElement => {
  const { t } = useTranslation()

  return (
    <Page>
      <Page.Heading title={t('Activity')}>
        <WorkflowSelect />
      </Page.Heading>
      <ActivityListContent />
    </Page>
  )
}

const ActivityListContent = (): ReactElement => {
  const { t } = useTranslation()
  const accountDrawer = useAccountDrawer()
  const clientState = useClientState()
  const serverState = useInfiniteServerState(clientState)
  const { isFetching, refetch, data } = serverState
  const { query, setQuery, selectedColumns, sorting, setSorting, clientFilter, range } = clientState

  return (
    <>
      <Page.FunctionBar>
        <div className='gap-2xl flex w-full flex-col'>
          <Group justify='space-between'>
            <Group gap='sm'>
              <TextInput placeholder={t('Search')} value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
              {isNotEmpty(serverState.total) && (
                <Text c='dimmed'>{toShowingCountText(data.length, serverState.total, 'activity', 'activities')}</Text>
              )}
            </Group>
            <Group gap='sm'>
              <ToggleActivityColumns {...clientState} />
              <DownloadActivityCsv clientFilter={clientFilter} range={range} data={data} />
              <ButtonRefresh isFetching={isFetching} refetch={refetch} />
            </Group>
          </Group>
          <ActivityFilters {...clientState} />
        </div>
      </Page.FunctionBar>
      <Page.Content>
        <TanStackActivityTable
          selectedColumns={selectedColumns}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={(row) => accountDrawer.open(row.accountId)}
          {...serverState}
        />
      </Page.Content>
      <AccountModal accountId={accountDrawer.state} onClose={accountDrawer.close} />
    </>
  )
}

export const Route = createFileRoute('/activity/')({
  component: Activity,
})
